import {
  IconArmchair,
  IconArmchair2,
  IconBed,
  IconBook,
  IconBox,
  IconBrush,
  IconBuildingBank,
  IconBuildingWarehouse,
  IconChartArea,
  IconChefHat,
  IconChevronDown,
  IconChevronRight,
  IconCommand,
  IconCreditCard,
  IconDeviceIpadHorizontal,
  IconDeviceTablet,
  IconDevices,
  IconDiscount2,
  IconFileInvoice,
  IconFriends,
  IconGift,
  IconHanger,
  IconInfoSquareRounded,
  IconLayoutDashboard,
  IconLifebuoy,
  IconLogout,
  IconReceipt,
  IconReceiptTax,
  IconReportMoney,
  IconScale,
  IconSearch,
  IconSettings2,
  IconStars,
  IconToolsKitchen3,
  IconTrashOff,
  IconTruck,
  IconUser,
  IconUsersGroup,
  IconX,
} from "@tabler/icons-react";
import { Fragment, useEffect, useState, useRef } from "react";
import { iconStroke, isElectronApp, getSyncMode } from "../config/config";
import AvatarImg from "../assets/avatar.svg";
import { Menu, Transition } from "@headlessui/react";
import { signOut } from "../controllers/auth.controller";
import { toast } from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import { getUserDetailsInLocalStorage } from "../helpers/UserDetails";
import { SCOPES } from "../config/scopes";
import AppBarDropdown from "./AppBarDropdown";
import { useTranslation } from "react-i18next";
import { useTheme } from "../contexts/ThemeContext";
import { useStoreSettings } from "../controllers/settings.controller";

const SafeIconPrinter = ({ size = 24, stroke = 2, className = "", ...props }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
    <path d="M17 17h2a2 2 0 0 0 2 -2v-4a2 2 0 0 0 -2 -2h-14a2 2 0 0 0 -2 2v4a2 2 0 0 0 2 2h2" />
    <path d="M17 9v-4a2 2 0 0 0 -2 -2h-6a2 2 0 0 0 -2 2v4" />
    <path d="M7 13m0 2a2 2 0 0 1 2 -2h6a2 2 0 0 1 2 2v4a2 2 0 0 1 -2 2h-6a2 2 0 0 1 -2 -2z" />
  </svg>
);

// Sync status badge for offline_sync mode in Electron
function SyncStatusBadge({ isOnline, navigate }) {
  const [syncStatus, setSyncStatus] = useState({ pendingCount: 0, isSyncing: false, lastSyncTime: null });

  useEffect(() => {
    // Poll sync status from SyncManager via IPC
    const fetchStatus = async () => {
      try {
        if (window.electron?.ipcRenderer?.invoke) {
          const status = await window.electron.ipcRenderer.invoke('get-sync-status');
          if (status) {
            setSyncStatus(prev => ({
              ...prev,
              isSyncing: status.isSyncing || false,
              lastSyncTime: status.lastSyncTime || null,
            }));
          }
        }
        // Also fetch pending count from local API
        if (window.electron?.ipcRenderer?.invoke) {
          const pending = await window.electron.ipcRenderer.invoke('get-pending-sync-count');
          if (typeof pending === 'number') {
            setSyncStatus(prev => ({ ...prev, pendingCount: pending }));
          }
        }
      } catch (e) {}
    };

    fetchStatus();
    const interval = setInterval(fetchStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleSyncNow = async (e) => {
    e.stopPropagation();
    try {
      if (window.electron?.ipcRenderer?.invoke) {
        setSyncStatus(prev => ({ ...prev, isSyncing: true }));
        await window.electron.ipcRenderer.invoke('trigger-sync');
        // Re-fetch status after sync
        setTimeout(async () => {
          try {
            const status = await window.electron.ipcRenderer.invoke('get-sync-status');
            const pending = await window.electron.ipcRenderer.invoke('get-pending-sync-count');
            setSyncStatus({
              isSyncing: status?.isSyncing || false,
              lastSyncTime: status?.lastSyncTime || null,
              pendingCount: typeof pending === 'number' ? pending : 0,
            });
          } catch (e) {}
        }, 1500);
      }
    } catch (e) {
      console.error('[SyncStatusBadge] Sync now failed:', e);
      setSyncStatus(prev => ({ ...prev, isSyncing: false }));
    }
  };

  const { pendingCount, isSyncing } = syncStatus;
  const hasPending = pendingCount > 0;

  let statusText = 'Offline-Ready';
  let dotColor = 'bg-green-500 animate-pulse';
  let badgeClass = 'bg-green-50 text-green-800 border-green-200 dark:bg-green-950/40 dark:text-green-300 dark:border-green-800';

  if (!isOnline) {
    statusText = hasPending ? `Offline (${pendingCount} pending)` : 'Offline Mode';
    dotColor = 'bg-amber-500';
    badgeClass = 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800';
  } else if (isSyncing) {
    statusText = 'Syncing...';
    dotColor = 'bg-blue-500 animate-pulse';
    badgeClass = 'bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800';
  } else if (hasPending) {
    statusText = `${pendingCount} pending`;
    dotColor = 'bg-amber-500 animate-pulse';
    badgeClass = 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800';
  } else {
    statusText = 'All Synced';
  }

  return (
    <div className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium shadow-sm border ${badgeClass}`}>
      <div 
        onClick={() => navigate('/dashboard/settings/workflow')} 
        className="flex items-center gap-2 cursor-pointer"
        title="Click to change workflow mode"
      >
        <span className={`w-2 h-2 rounded-full ${dotColor}`} />
        <span>{statusText}</span>
      </div>
      {isOnline && (hasPending || isSyncing) && (
        <button 
          onClick={handleSyncNow}
          disabled={isSyncing}
          className={`ml-1 px-2 py-0.5 rounded-full text-[10px] font-bold transition ${
            isSyncing 
              ? 'bg-blue-200 text-blue-600 dark:bg-blue-900 dark:text-blue-300 cursor-wait' 
              : 'bg-green-200 text-green-800 dark:bg-green-900 dark:text-green-300 hover:bg-green-300 cursor-pointer'
          }`}
          title="Sync pending changes now"
        >
          {isSyncing ? '⟳' : 'Sync Now'}
        </button>
      )}
    </div>
  );
}


export default function AppBar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isOnline, setIsOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const { theme } = useTheme();
  const searchInputRef = useRef(null);
  const itemRefs = useRef([]);

  const [user, setUser] = useState(() => getUserDetailsInLocalStorage() || {});
  const { role: userRole, scope } = user;
  const userScopes = scope ? String(scope).split(",").map(s => s.trim()) : [];
  const { data: storeSettings } = useStoreSettings();

  useEffect(() => {
    const handleAuthChange = () => {
      setUser(getUserDetailsInLocalStorage() || {});
    };
    window.addEventListener("auth_state_changed", handleAuthChange);
    return () => window.removeEventListener("auth_state_changed", handleAuthChange);
  }, []);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const btnShowSearchModal = () => {
    setSearch("");
    setSelectedIndex(0);
    const modal = document.getElementById("search-modal");
    if (modal) {
      if (typeof modal.showModal === "function") {
        modal.showModal();
      }
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  };

  const handleNavigate = (link) => {
    const modal = document.getElementById("search-modal");
    if (modal && typeof modal.close === "function") {
      modal.close();
    }
    navigate(link);
  };

  useEffect(() => {
    const down = (e) => {
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        btnShowSearchModal();
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const isModuleEnabled = (moduleKey) => {
    if (!moduleKey) return true;
    const modulesStr = (storeSettings?.enabled_modules !== undefined && storeSettings?.enabled_modules !== null)
      ? storeSettings.enabled_modules
      : user.enabled_modules;

    if (modulesStr === undefined || modulesStr === null) return true;
    const enabledList = modulesStr.split(",").map(m => m.trim());

    if (moduleKey === "requests") {
      return enabledList.includes("requests") || enabledList.includes("orders") || enabledList.includes("pos");
    }

    if (moduleKey === "report_order_deletion" && (enabledList.includes("report_order_deletion") || enabledList.includes("report_orderdeletion"))) {
      return true;
    }

    if (moduleKey === "report_discounts" || moduleKey === "report_discounts_deductions") {
      return enabledList.includes("report_discounts") || enabledList.includes("report_discounts_deductions") || enabledList.includes("reports");
    }

    if (moduleKey.startsWith("report_")) {
      if (enabledList.includes(moduleKey)) return true;
      if (enabledList.includes("reports")) return true;
    }

    if (moduleKey === "assets" || moduleKey === "hotel_assets") {
      return enabledList.includes("assets") || enabledList.includes("hotel_assets");
    }

    return enabledList.includes(moduleKey);
  };

  const searchItems = [
    // --- Core Operations ---
    {
      title: t("appbar.dashboard") || "Dashboard",
      category: "Core Operations",
      description: "Overview metrics, stats & business insights",
      icon: <IconLayoutDashboard stroke={iconStroke} size={20} />,
      link: "/dashboard/home",
      scopes: [SCOPES.DASHBOARD],
      keywords: "home dashboard overview metrics stats analytics main",
    },
    {
      title: t("navbar.pos") || "POS (Point of Sale)",
      category: "Core Operations",
      description: "Take dine-in, takeaway and delivery orders",
      icon: <IconDeviceIpadHorizontal stroke={iconStroke} size={20} />,
      link: "/dashboard/pos",
      scopes: [SCOPES.POS],
      module: "pos",
      keywords: "pos point of sale billing take order table new order cart billing checkout",
    },
    {
      title: t("navbar.orders") || "Orders & KDS",
      category: "Core Operations",
      description: "View, update and manage active kitchen orders",
      icon: <IconToolsKitchen3 stroke={iconStroke} size={20} />,
      link: "/dashboard/orders",
      scopes: [SCOPES.POS, SCOPES.ORDERS, SCOPES.ORDER_STATUS, SCOPES.ORDER_STATUS_DISPLAY],
      module: "orders",
      keywords: "orders active orders running orders kitchen display kot live orders bills",
    },
    {
      title: "Delivery & Rooms",
      category: "Core Operations",
      description: "Online delivery requests & hotel room orders",
      icon: <IconTruck stroke={iconStroke} size={20} />,
      link: "/dashboard/requests",
      scopes: [SCOPES.POS, SCOPES.ORDERS],
      module: "requests",
      keywords: "delivery rooms requests room service online orders takeaway rider dispatch",
    },
    {
      title: t("navbar.kitchen") || "Kitchen Display",
      category: "Core Operations",
      description: "Chef KDS screen for food preparation",
      icon: <IconChefHat stroke={iconStroke} size={20} />,
      link: "/dashboard/kitchen",
      scopes: [SCOPES.KITCHEN, SCOPES.KITCHEN_DISPLAY],
      module: "kitchen",
      keywords: "kitchen display kds chef cook prepare kot tickets kitchen screen",
    },
    {
      title: t("navbar.reservation") || "Table Reservations",
      category: "Core Operations",
      description: "Manage table bookings & guest schedules",
      icon: <IconArmchair2 stroke={iconStroke} size={20} />,
      link: "/dashboard/reservation",
      scopes: [SCOPES.RESERVATIONS, SCOPES.VIEW_RESERVATIONS, SCOPES.MANAGE_RESERVATIONS],
      module: "reservation",
      keywords: "reservation book table booking guests schedule seating",
    },
    {
      title: t("navbar.customers") || "Customers Directory",
      category: "Core Operations",
      description: "Customer contacts, history & credit dues",
      icon: <IconFriends stroke={iconStroke} size={20} />,
      link: "/dashboard/customers",
      scopes: [SCOPES.CUSTOMERS, SCOPES.VIEW_CUSTOMERS, SCOPES.MANAGE_CUSTOMERS],
      module: "customers",
      keywords: "customers clients guest directory contact credit loyalty",
    },
    {
      title: "Import Customers",
      category: "Core Operations",
      description: "Bulk upload customer list via CSV/Excel",
      icon: <IconFriends stroke={iconStroke} size={20} />,
      link: "/dashboard/customers/import",
      scopes: [SCOPES.CUSTOMERS, SCOPES.VIEW_CUSTOMERS, SCOPES.MANAGE_CUSTOMERS],
      module: "customers",
      keywords: "import customers csv excel bulk upload clients",
    },

    // --- PMS Operations ---
    {
      title: "Room Status Grid",
      category: "PMS Operations",
      description: "Hotel room occupancy, check-in & check-out",
      icon: <IconBed stroke={iconStroke} size={20} />,
      link: "/dashboard/pms/rooms",
      scopes: [SCOPES.PMS, SCOPES.ROOMS],
      module: "pms",
      keywords: "pms rooms hotel room status grid check in check out lodging guests stay",
    },
    {
      title: "Housekeeping",
      category: "PMS Operations",
      description: "Room cleaning status, inspection & housekeeping logs",
      icon: <IconBrush stroke={iconStroke} size={20} />,
      link: "/dashboard/pms/housekeeping",
      scopes: [SCOPES.PMS, SCOPES.HOUSEKEEPING],
      module: "pms",
      keywords: "housekeeping cleaning clean dirty rooms inspect maintenance tidy",
    },
    {
      title: "Laundry & Linen",
      category: "PMS Operations",
      description: "Hotel laundry, linen tracking & dry cleaning",
      icon: <IconHanger stroke={iconStroke} size={20} />,
      link: "/dashboard/pms/laundry",
      scopes: [SCOPES.PMS, SCOPES.LAUNDRY],
      module: "pms",
      keywords: "laundry linen towels bedsheet wash iron dry cleaning clothes",
    },

    // --- Inventory & Supply ---
    {
      title: "Inventory Stock",
      category: "Inventory & Supply",
      description: "Track raw materials, stock quantities & low stock alerts",
      icon: <IconBuildingWarehouse stroke={iconStroke} size={20} />,
      link: "/dashboard/inventory",
      scopes: [SCOPES.INVENTORY, SCOPES.VIEW_INVENTORY, SCOPES.MANAGE_INVENTORY],
      module: "inventory",
      keywords: "inventory stock raw materials items quantity ingredients stock alert items",
    },
    {
      title: "Inventory Dashboard",
      category: "Inventory & Supply",
      description: "Visual stock valuation, movements & alerts",
      icon: <IconBuildingWarehouse stroke={iconStroke} size={20} />,
      link: "/dashboard/inventory/dashboard",
      scopes: [SCOPES.INVENTORY, SCOPES.VIEW_INVENTORY, SCOPES.MANAGE_INVENTORY],
      module: "inventory",
      keywords: "inventory dashboard stock valuation analytics low stock overview",
    },
    {
      title: "Stock Movements & Logs",
      category: "Inventory & Supply",
      description: "Audit trail of all item consumption, adjustments & waste",
      icon: <IconBuildingWarehouse stroke={iconStroke} size={20} />,
      link: "/dashboard/inventory/stock-movements",
      scopes: [SCOPES.INVENTORY, SCOPES.VIEW_INVENTORY, SCOPES.MANAGE_INVENTORY],
      module: "inventory",
      keywords: "stock movements logs audit trail waste consumption adjustments",
    },
    {
      title: "Purchases",
      category: "Inventory & Supply",
      description: "Supplier purchase bills & procurement history",
      icon: <IconBuildingWarehouse stroke={iconStroke} size={20} />,
      link: "/dashboard/inventory/purchases",
      scopes: [SCOPES.INVENTORY, SCOPES.PURCHASES, SCOPES.PURCHASE_INVOICES],
      module: "inventory",
      keywords: "purchases purchase orders buy stock vendor bills procurement",
    },
    {
      title: "Suppliers & Vendors",
      category: "Inventory & Supply",
      description: "Vendor directory, balances & contact details",
      icon: <IconUsersGroup stroke={iconStroke} size={20} />,
      link: "/dashboard/inventory/suppliers",
      scopes: [SCOPES.INVENTORY, SCOPES.SUPPLIERS],
      module: "inventory",
      keywords: "suppliers vendors vendor list supplier balance contacts",
    },
    {
      title: "Expenses",
      category: "Inventory & Supply",
      description: "Operational spending, bills & petty cash outflows",
      icon: <IconFileInvoice stroke={iconStroke} size={20} />,
      link: "/dashboard/expenses",
      scopes: [SCOPES.EXPENSES, SCOPES.REPORTS, SCOPES.INVENTORY],
      module: "inventory",
      keywords: "expenses operational expenses petty cash bills spending payout costs",
    },
    {
      title: "Import Inventory",
      category: "Inventory & Supply",
      description: "Bulk import items & stock via Excel/CSV",
      icon: <IconBuildingWarehouse stroke={iconStroke} size={20} />,
      link: "/dashboard/inventory/import",
      scopes: [SCOPES.INVENTORY, SCOPES.MANAGE_INVENTORY],
      module: "inventory",
      keywords: "import inventory bulk stock csv excel upload items",
    },

    // --- Accounting & Finance ---
    {
      title: "Bank Accounts",
      category: "Accounting & Finance",
      description: "Manage bank accounts, deposits, transfers & cash balances",
      icon: <IconBuildingBank stroke={iconStroke} size={20} />,
      link: "/dashboard/accounting/banks",
      scopes: [SCOPES.ACCOUNTING, SCOPES.INVOICES, SCOPES.SETTINGS],
      module: "accounting_banks",
      keywords: "bank accounts banking cash transfer deposit balance finance",
    },
    {
      title: t("navbar.invoices") || "Invoices & Receipts",
      category: "Accounting & Finance",
      description: "View issued tax invoices, print receipts & refunds",
      icon: <IconFileInvoice stroke={iconStroke} size={20} />,
      link: "/dashboard/invoices",
      scopes: [SCOPES.INVOICES],
      module: "invoices",
      keywords: "invoices receipts bills tax invoice payment history refunds",
    },
    {
      title: "Accounts Receivable",
      category: "Accounting & Finance",
      description: "Customer credit dues, debtor ledgers & pending payments",
      icon: <IconBuildingWarehouse stroke={iconStroke} size={20} />,
      link: "/dashboard/accounts-receivable",
      scopes: [SCOPES.INVOICES, SCOPES.ACCOUNTING, SCOPES.REPORTS],
      module: "accounts_receivable",
      keywords: "accounts receivable credit debtors customer balance due collection",
    },
    {
      title: "Accounts Payable",
      category: "Accounting & Finance",
      description: "Outstanding supplier dues & unpaid purchase bills",
      icon: <IconBuildingWarehouse stroke={iconStroke} size={20} />,
      link: "/dashboard/inventory/accounts-payable",
      scopes: [SCOPES.INVENTORY, SCOPES.PURCHASES, SCOPES.PURCHASE_INVOICES, SCOPES.ACCOUNTING],
      module: "accounts_payable",
      keywords: "accounts payable creditors supplier dues pending vendor payments",
    },

    // --- Reports & Analytics ---
    {
      title: t("navbar.reports") || "Reports Overview",
      category: "Reports & Analytics",
      description: "Central reporting dashboard & business insights",
      icon: <IconChartArea stroke={iconStroke} size={20} />,
      link: "/dashboard/reports",
      scopes: [SCOPES.REPORTS],
      module: "reports",
      keywords: "reports analytics overview sales summary all reports",
    },
    {
      title: "Day Book Report",
      category: "Reports & Analytics",
      description: "Shift sales register, daily cash collections & day book",
      icon: <IconReportMoney stroke={iconStroke} size={20} />,
      link: "/dashboard/reports/day-book",
      scopes: [SCOPES.REPORTS, SCOPES.DAYBOOK],
      module: "report_daybook",
      keywords: "day book daily sales shift cashier register close day cash summary",
    },
    {
      title: "Revenue Reports",
      category: "Reports & Analytics",
      description: "Total revenue, sales trends & earnings analysis",
      icon: <IconChartArea stroke={iconStroke} size={20} />,
      link: "/dashboard/reports/revenue",
      scopes: [SCOPES.REPORTS],
      module: "report_revenue",
      keywords: "revenue report sales trends earnings income sales",
    },
    {
      title: "Front Office Reports",
      category: "Reports & Analytics",
      description: "Front office arrivals, departures & guest stays",
      icon: <IconChartArea stroke={iconStroke} size={20} />,
      link: "/dashboard/reports/front-office",
      scopes: [SCOPES.REPORTS],
      module: "report_frontoffice",
      keywords: "front office report reception arrivals departures guest stay",
    },
    {
      title: "Occupancy Reports",
      category: "Reports & Analytics",
      description: "Room occupancy rates, room nights & hotel capacity",
      icon: <IconChartArea stroke={iconStroke} size={20} />,
      link: "/dashboard/reports/occupancy",
      scopes: [SCOPES.REPORTS],
      module: "report_occupancy",
      keywords: "occupancy report hotel occupancy room nights booking rates",
    },
    {
      title: "Guest Reports",
      category: "Reports & Analytics",
      description: "Guest history, demographics & repeat visits",
      icon: <IconChartArea stroke={iconStroke} size={20} />,
      link: "/dashboard/reports/guest",
      scopes: [SCOPES.REPORTS],
      module: "report_guest",
      keywords: "guest report customer statistics guest demographics",
    },
    {
      title: "Housekeeping Reports",
      category: "Reports & Analytics",
      description: "Cleaning logs, staff inspections & room turnover",
      icon: <IconChartArea stroke={iconStroke} size={20} />,
      link: "/dashboard/reports/housekeeping",
      scopes: [SCOPES.REPORTS],
      module: "report_housekeeping",
      keywords: "housekeeping report cleaning logs staff room turnaround",
    },
    {
      title: "Laundry & Linen Reports",
      category: "Reports & Analytics",
      description: "Linen usage, laundry batches & laundry turnover",
      icon: <IconChartArea stroke={iconStroke} size={20} />,
      link: "/dashboard/reports/laundry",
      scopes: [SCOPES.REPORTS],
      module: "report_laundry",
      keywords: "laundry report linen usage batch turnover",
    },
    {
      title: "F&B Reports (Food & Beverage)",
      category: "Reports & Analytics",
      description: "Dish sales, top-selling menu items & dining revenue",
      icon: <IconChartArea stroke={iconStroke} size={20} />,
      link: "/dashboard/reports/fb",
      scopes: [SCOPES.REPORTS],
      module: "report_fb",
      keywords: "fb food beverage restaurant sales top dishes menu performance",
    },
    {
      title: "Inventory Reports",
      category: "Reports & Analytics",
      description: "Closing stock balances & inventory valuation",
      icon: <IconChartArea stroke={iconStroke} size={20} />,
      link: "/dashboard/reports/inventory",
      scopes: [SCOPES.REPORTS],
      module: "report_inventory",
      keywords: "inventory report stock valuation raw materials",
    },
    {
      title: "Purchase Reports",
      category: "Reports & Analytics",
      description: "Vendor procurement summary & purchase spending",
      icon: <IconChartArea stroke={iconStroke} size={20} />,
      link: "/dashboard/reports/purchase",
      scopes: [SCOPES.REPORTS],
      module: "report_purchase",
      keywords: "purchase report vendor bills procurement costs",
    },
    {
      title: "Finance Reports",
      category: "Reports & Analytics",
      description: "Cash flow, financial health & net margins",
      icon: <IconChartArea stroke={iconStroke} size={20} />,
      link: "/dashboard/reports/finance",
      scopes: [SCOPES.REPORTS],
      module: "report_finance",
      keywords: "finance report cash flow margins profit overview",
    },
    {
      title: "HR & Staff Reports",
      category: "Reports & Analytics",
      description: "Staff payroll, attendance & staff performance",
      icon: <IconChartArea stroke={iconStroke} size={20} />,
      link: "/dashboard/reports/hr",
      scopes: [SCOPES.REPORTS],
      module: "report_hr",
      keywords: "hr report staff attendance payroll hours",
    },
    {
      title: "Maintenance Reports",
      category: "Reports & Analytics",
      description: "Equipment repair requests & facility maintenance",
      icon: <IconChartArea stroke={iconStroke} size={20} />,
      link: "/dashboard/reports/maintenance",
      scopes: [SCOPES.REPORTS],
      module: "report_maintenance",
      keywords: "maintenance report repairs equipment facilities work orders",
    },
    {
      title: "Guest Service Reports",
      category: "Reports & Analytics",
      description: "Special guest requests, fulfillment & satisfaction",
      icon: <IconChartArea stroke={iconStroke} size={20} />,
      link: "/dashboard/reports/guest-service",
      scopes: [SCOPES.REPORTS],
      module: "report_guestservice",
      keywords: "guest service report service requests guest satisfaction",
    },
    {
      title: "Audit Reports",
      category: "Reports & Analytics",
      description: "System security logs & operational audit trail",
      icon: <IconChartArea stroke={iconStroke} size={20} />,
      link: "/dashboard/reports/audit",
      scopes: [SCOPES.REPORTS],
      module: "report_audit",
      keywords: "audit report system logs security actions trail",
    },
    {
      title: "Executive Reports",
      category: "Reports & Analytics",
      description: "Executive high-level KPI dashboard & summary",
      icon: <IconChartArea stroke={iconStroke} size={20} />,
      link: "/dashboard/reports/executive",
      scopes: [SCOPES.REPORTS],
      module: "report_executive",
      keywords: "executive report kpi high level summary board management",
    },
    {
      title: "General Ledger",
      category: "Reports & Analytics",
      description: "Complete journal debit & credit accounting ledgers",
      icon: <IconChartArea stroke={iconStroke} size={20} />,
      link: "/dashboard/reports/ledger",
      scopes: [SCOPES.REPORTS],
      module: "report_ledger",
      keywords: "general ledger journal debit credit accounting books entries",
    },
    {
      title: "Profit & Loss Statement",
      category: "Reports & Analytics",
      description: "Revenue minus expenses, net profit & profit margins",
      icon: <IconChartArea stroke={iconStroke} size={20} />,
      link: "/dashboard/reports/profit-loss",
      scopes: [SCOPES.REPORTS],
      module: "report_profitloss",
      keywords: "profit loss pnl income statement net profit revenue costs",
    },
    {
      title: "Trial Balance",
      category: "Reports & Analytics",
      description: "Summary debit & credit closing balances for all accounts",
      icon: <IconScale stroke={iconStroke} size={20} />,
      link: "/dashboard/reports/trial-balance",
      scopes: [SCOPES.REPORTS],
      module: "report_trialbalance",
      keywords: "trial balance closing balance debits credits ledger check",
    },
    {
      title: "Balance Sheet",
      category: "Reports & Analytics",
      description: "Assets, liabilities and equity statement",
      icon: <IconBuildingBank stroke={iconStroke} size={20} />,
      link: "/dashboard/reports/balance-sheet",
      scopes: [SCOPES.REPORTS],
      module: "report_balancesheet",
      keywords: "balance sheet assets liabilities equity financial net worth",
    },
    {
      title: "KOT Report",
      category: "Reports & Analytics",
      description: "Printed Kitchen Order Tickets history & audit",
      icon: <IconReceipt stroke={iconStroke} size={20} />,
      link: "/dashboard/reports/kot-report",
      scopes: [SCOPES.REPORTS],
      module: "report_kot",
      keywords: "kot report kitchen order tickets print log printed kots",
    },
    {
      title: "Order Deletion Report",
      category: "Reports & Analytics",
      description: "Voided items, deleted orders & cancellation reasons",
      icon: <IconTrashOff stroke={iconStroke} size={20} />,
      link: "/dashboard/reports/order-deletion-report",
      scopes: [SCOPES.REPORTS],
      module: "report_order_deletion",
      keywords: "order deletion report cancelled orders void deleted items reasons",
    },
    {
      title: "Discounts & Round Off Report",
      category: "Reports & Analytics",
      description: "All discounts granted, round-offs & bill deductions",
      icon: <IconDiscount2 stroke={iconStroke} size={20} />,
      link: "/dashboard/reports/discounts-deductions",
      scopes: [SCOPES.REPORTS],
      module: "report_discounts",
      keywords: "discounts round off report deductions price concessions waivers",
    },
    {
      title: "Voided Invoices Report",
      category: "Reports & Analytics",
      description: "Reversed and voided bill invoice records",
      icon: <IconTrashOff stroke={iconStroke} size={20} />,
      link: "/dashboard/reports/voided-invoices",
      scopes: [SCOPES.REPORTS],
      module: "reports",
      keywords: "voided invoices cancelled bills reversals invalid bills",
    },

    // --- Asset Management ---
    {
      title: "Hotel Assets",
      category: "Asset Management",
      description: "Track physical property assets, equipment & furniture",
      icon: <IconBox stroke={iconStroke} size={20} />,
      link: "/dashboard/assets",
      scopes: [SCOPES.ASSETS, SCOPES.SETTINGS, SCOPES.INVENTORY],
      module: "assets",
      keywords: "hotel assets fixed assets equipment furniture items property asset tracking",
    },

    // --- Back Office ---
    {
      title: t("navbar.feedbacks") || "Customer Feedback",
      category: "Back Office",
      description: "Guest reviews, star ratings & suggestions",
      icon: <IconStars stroke={iconStroke} size={20} />,
      link: "/dashboard/feedbacks",
      scopes: [SCOPES.FEEDBACK],
      module: "feedbacks",
      keywords: "feedbacks reviews ratings comments satisfaction stars",
    },
    {
      title: t("navbar.users") || "Staff & Users",
      category: "Back Office",
      description: "Create employee accounts, assign roles & access permissions",
      icon: <IconUsersGroup stroke={iconStroke} size={20} />,
      link: "/dashboard/users",
      scopes: [SCOPES.SETTINGS],
      keywords: "users staff employees cashiers waiters roles permissions passwords team",
    },
    {
      title: "HR & Payroll",
      category: "Back Office",
      description: "Salary slips, payroll management & employee records",
      icon: <IconUsersGroup stroke={iconStroke} size={20} />,
      link: "/dashboard/payroll",
      scopes: [SCOPES.PAYROLL, SCOPES.SETTINGS],
      module: "hr",
      keywords: "payroll hr salary wages payslip compensation employees",
    },

    // --- Settings & Configuration ---
    {
      title: t("appbar.store_settings") || "Store Details & Settings",
      category: "Settings",
      description: "Business name, phone, address, currency & offline mode",
      icon: <IconSettings2 stroke={iconStroke} size={20} />,
      link: "/dashboard/settings",
      scopes: [SCOPES.SETTINGS],
      keywords: "settings store details profile address logo currency business info config",
    },
    {
      title: t("appbar.print_settings") || "Print Settings",
      category: "Settings",
      description: "Invoice template, receipt headers, footers & paper sizes",
      icon: <SafeIconPrinter stroke={iconStroke} size={20} />,
      link: "/dashboard/settings/print-settings",
      scopes: [SCOPES.SETTINGS],
      keywords: "print settings receipt format bill template header footer kot size",
    },
    {
      title: t("appbar.store_tables") || "Dining Tables Setup",
      category: "Settings",
      description: "Configure restaurant tables, seating capacity & QR codes",
      icon: <IconArmchair2 stroke={iconStroke} size={20} />,
      link: "/dashboard/settings/tables",
      scopes: [SCOPES.SETTINGS],
      keywords: "tables dining tables restaurant seating floor plan qr code table setup",
    },
    {
      title: "Hotel Rooms Setup",
      category: "Settings",
      description: "Configure room categories, room numbers & pricing",
      icon: <IconBed stroke={iconStroke} size={20} />,
      link: "/dashboard/settings/rooms",
      scopes: [SCOPES.SETTINGS],
      keywords: "rooms room setup hotel rooms room types room pricing amenities",
    },
    {
      title: t("appbar.menu_items") || "Menu Items & Catalog",
      category: "Settings",
      description: "Food menu items, dish pricing, variants & recipes",
      icon: <IconBook stroke={iconStroke} size={20} />,
      link: "/dashboard/settings/menu-items",
      scopes: [SCOPES.SETTINGS],
      keywords: "menu items dishes food pricing catalog recipes variants products",
    },
    {
      title: "Menu Categories",
      category: "Settings",
      description: "Create and organize food & beverage categories",
      icon: <IconBook stroke={iconStroke} size={20} />,
      link: "/dashboard/settings/menu-items/categories",
      scopes: [SCOPES.SETTINGS],
      keywords: "categories menu categories food groups sections",
    },
    {
      title: "Import Menu Items",
      category: "Settings",
      description: "Bulk import menu items from CSV or Excel file",
      icon: <IconBook stroke={iconStroke} size={20} />,
      link: "/dashboard/settings/menu-items/import",
      scopes: [SCOPES.SETTINGS],
      keywords: "import menu items bulk import csv excel dishes catalog",
    },
    {
      title: t("appbar.tax_setup") || "Tax Setup (VAT / PAN)",
      category: "Settings",
      description: "Configure VAT percentages, PAN numbers & service charge",
      icon: <IconReceiptTax stroke={iconStroke} size={20} />,
      link: "/dashboard/settings/tax-setup",
      scopes: [SCOPES.SETTINGS],
      keywords: "tax vat pan tax rate service charge tax setup government tax",
    },
    {
      title: "Payment Types Setup",
      category: "Settings",
      description: "Setup payment options (Cash, Fonepay, Card, eSewa, etc.)",
      icon: <IconCreditCard stroke={iconStroke} size={20} />,
      link: "/dashboard/settings/payment-types",
      scopes: [SCOPES.SETTINGS],
      keywords: "payment types cash card fonepay esewa khalti qr credit bank transfer",
    },
    {
      title: "Loyalty Program Setup",
      category: "Settings",
      description: "Setup customer loyalty points, rewards & redemption rules",
      icon: <IconGift stroke={iconStroke} size={20} />,
      link: "/dashboard/settings/loyalty",
      scopes: [SCOPES.SETTINGS],
      keywords: "loyalty program reward points customer discounts rewards cashback",
    },
    {
      title: "Printers Setup",
      category: "Settings",
      description: "Configure network LAN, USB and Bluetooth thermal printers",
      icon: <SafeIconPrinter stroke={iconStroke} size={20} />,
      link: "/dashboard/settings/printers",
      scopes: [SCOPES.SETTINGS],
      keywords: "printers thermal printer network kitchen printer usb bluetooth pos printer",
    },

    // --- Account & System ---
    {
      title: t("appbar.devices") || "Connected Devices",
      category: "Account & System",
      description: "View active login sessions & connected tablets",
      icon: <IconDevices stroke={iconStroke} size={20} />,
      link: "/dashboard/devices",
      scopes: [],
      keywords: "devices connected devices tablets sessions phones logins",
    },
    {
      title: t("appbar.profile") || "My Profile",
      category: "Account & System",
      description: "Account details, name & change password",
      icon: <IconUser stroke={iconStroke} size={20} />,
      link: "/dashboard/profile",
      scopes: [],
      keywords: "profile account user details change password my account",
    },
    {
      title: "Language Settings",
      category: "Account & System",
      description: "Switch app display language (English / Nepali)",
      icon: <IconSettings2 stroke={iconStroke} size={20} />,
      link: "/dashboard/language",
      scopes: [],
      keywords: "language nepali english translation switch language",
    },
    {
      title: t("appbar.support") || "Contact Support",
      category: "Account & System",
      description: "Get technical support, manuals & help documentation",
      icon: <IconLifebuoy stroke={iconStroke} size={20} />,
      link: "/dashboard/contact-support",
      scopes: [],
      keywords: "support help contact customer support technical assistance manuals",
    },
  ];

  const allowedItems = searchItems.filter((item) => {
    if (!isModuleEnabled(item.module)) return false;
    if (userRole === "admin") return true;
    const requiredScopes = item.scopes;
    if (!requiredScopes || requiredScopes.length === 0) return true;
    return requiredScopes.some((scope) => userScopes && userScopes.includes(scope));
  });

  const query = search.trim().toLowerCase();
  const filteredResults = allowedItems.filter((item) => {
    if (!query) return true;
    const titleMatch = item.title.toLowerCase().includes(query);
    const categoryMatch = item.category?.toLowerCase().includes(query);
    const descMatch = item.description?.toLowerCase().includes(query);
    const keywordMatch = item.keywords?.toLowerCase().includes(query);
    const linkMatch = item.link?.toLowerCase().includes(query);
    return titleMatch || categoryMatch || descMatch || keywordMatch || linkMatch;
  });

  useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  useEffect(() => {
    if (itemRefs.current[selectedIndex]) {
      itemRefs.current[selectedIndex].scrollIntoView({
        block: "nearest",
        behavior: "smooth"
      });
    }
  }, [selectedIndex]);

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      const modal = document.getElementById("search-modal");
      if (modal && typeof modal.close === "function") {
        modal.close();
      }
      return;
    }

    if (filteredResults.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < filteredResults.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : filteredResults.length - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filteredResults[selectedIndex]) {
        handleNavigate(filteredResults[selectedIndex].link);
      }
    }
  };

  return (
    <>
      <div className="flex items-center justify-between px-4 py-3 border-b w-full sticky top-0 backdrop-blur-md z-[9999] border-restro-border-green">
        {/* search trigger button */}
        <button
          onClick={btnShowSearchModal}
          className="rounded-full flex items-center px-3.5 py-2 gap-2.5 bg-restro-green-light dark:bg-neutral-800 text-restro-text hover:opacity-90 transition border border-restro-border-green/20"
          title="Search menu (Ctrl + K)"
        >
          <IconSearch stroke={iconStroke} size={18} className="text-restro-green shrink-0" />
          <div
            className="bg-transparent outline-none text-start w-48 md:flex items-center justify-between hidden text-xs font-medium"
          >
            <p className="truncate text-gray-500 dark:text-gray-400">Search menus...</p>
            <div className="flex items-center gap-1 ml-2">
              <kbd className="kbd kbd-xs py-0.5 px-1.5 rounded text-[10px] bg-white dark:bg-neutral-700 shadow-sm border border-gray-200 dark:border-neutral-600 font-sans font-semibold">Ctrl</kbd>
              <kbd className="kbd kbd-xs py-0.5 px-1.5 rounded text-[10px] bg-white dark:bg-neutral-700 shadow-sm border border-gray-200 dark:border-neutral-600 font-sans font-semibold">K</kbd>
            </div>
          </div>
        </button>
        {/* search trigger button */}

        <div className="flex items-center gap-3">
          {/* Sync / Workflow Status Badge */}
          {isElectronApp() && getSyncMode() === 'offline_sync' && (
            <SyncStatusBadge isOnline={isOnline} navigate={navigate} />
          )}
          {isElectronApp() && getSyncMode() === 'online_only' && (
            <div 
              onClick={() => navigate('/dashboard/settings/workflow')}
              title="Online Only mode: All data from cloud server. Click to change."
              className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition hover:opacity-90 shadow-sm border ${
                isOnline
                  ? 'bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800'
                  : 'bg-red-50 text-red-800 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${isOnline ? 'bg-blue-500' : 'bg-red-500 animate-pulse'}`} />
              <span>{isOnline ? 'Online Only' : 'No Internet (Required)'}</span>
            </div>
          )}
          {!isElectronApp() && (
            <div 
              onClick={() => navigate('/dashboard/settings')}
              title="Cloud Status"
              className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition hover:opacity-90 shadow-sm border ${
                isOnline
                  ? 'bg-green-50 text-green-800 border-green-200 dark:bg-green-950/40 dark:text-green-300 dark:border-green-800'
                  : 'bg-red-50 text-red-800 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-500 animate-pulse' : 'bg-red-500 animate-pulse'}`} />
              <span>{isOnline ? 'Cloud Synced' : 'Offline Mode'}</span>
            </div>
          )}

          {/* profile */}
          <AppBarDropdown />
          {/* profile */}
        </div>
      </div>

      <dialog id="search-modal" className="modal modal-top sm:modal-middle backdrop-blur-sm transition-all duration-200">
        <div className={`modal-box p-0 overflow-hidden w-11/12 max-w-2xl max-h-[85vh] flex flex-col rounded-3xl shadow-2xl border ${
          theme === "black" 
            ? "bg-[#1e1e1e] text-white border-neutral-800" 
            : "bg-white text-restro-text border-restro-border-green-light"
        }`}>
          {/* Search Header */}
          <div className={`flex items-center px-4 py-3.5 border-b gap-3 sticky top-0 z-10 ${
            theme === "black" 
              ? "border-neutral-800 bg-[#252525]" 
              : "border-gray-100 bg-slate-50/90 backdrop-blur"
          }`}>
            <IconSearch stroke={iconStroke} className="text-gray-400 shrink-0" size={22} />
            <input
              ref={searchInputRef}
              type="text"
              className="bg-transparent outline-none flex-1 text-base placeholder:text-gray-400 text-restro-text"
              placeholder="Search menus (e.g. POS, Kitchen, Laundry, Reports, Settings)..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            {search && (
              <button 
                onClick={() => setSearch("")} 
                className="text-xs px-2.5 py-1 rounded-full text-gray-400 hover:text-gray-700 dark:hover:text-white bg-gray-100 dark:bg-neutral-700 transition"
              >
                Clear
              </button>
            )}
            <form method="dialog">
              <button className="btn btn-sm btn-circle btn-ghost text-gray-400 hover:text-gray-700 dark:hover:text-white">
                <IconX stroke={iconStroke} size={18} />
              </button>
            </form>
          </div>

          {/* Results List */}
          <div className="overflow-y-auto flex-1 p-2 divide-y divide-gray-50 dark:divide-neutral-800/40">
            {filteredResults.length === 0 ? (
              <div className="py-12 text-center text-gray-400 flex flex-col items-center justify-center gap-2">
                <IconSearch stroke={1.5} size={36} className="opacity-40" />
                <p className="text-sm font-medium">No menu items found for "{search}"</p>
                <p className="text-xs text-gray-400">Try searching for keywords like POS, Orders, Rooms, Reports, Settings...</p>
              </div>
            ) : (
              filteredResults.map((item, index) => {
                const isSelected = index === selectedIndex;
                return (
                  <div
                    key={item.link + index}
                    ref={(el) => (itemRefs.current[index] = el)}
                    onClick={() => handleNavigate(item.link)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={`flex items-center justify-between px-3.5 py-2.5 rounded-2xl cursor-pointer transition-all duration-150 ${
                      isSelected
                        ? theme === "black"
                          ? "bg-neutral-800 text-emerald-400 border border-emerald-500/30 pl-4.5 shadow-sm"
                          : "bg-emerald-50/90 text-emerald-900 border border-emerald-200/80 pl-4.5 shadow-sm"
                        : theme === "black"
                          ? "hover:bg-neutral-800/60 text-gray-200"
                          : "hover:bg-gray-100/80 text-gray-700"
                    }`}
                  >
                    <div className="flex items-center gap-3.5 min-w-0">
                      <div className={`p-2 rounded-xl shrink-0 transition-colors ${
                        isSelected
                          ? theme === "black" ? "bg-emerald-950/80 text-emerald-400" : "bg-emerald-100 text-emerald-700"
                          : theme === "black" ? "bg-neutral-800/80 text-gray-300" : "bg-gray-100 text-gray-600"
                      }`}>
                        {item.icon}
                      </div>
                      <div className="truncate">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm truncate">{item.title}</span>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold ${
                            isSelected
                              ? theme === "black" ? "bg-emerald-950 text-emerald-300 border border-emerald-800" : "bg-emerald-100 text-emerald-800"
                              : theme === "black" ? "bg-neutral-800 text-gray-400" : "bg-gray-100 text-gray-500"
                          }`}>
                            {item.category}
                          </span>
                        </div>
                        {item.description && (
                          <p className={`text-xs truncate mt-0.5 ${
                            isSelected 
                              ? theme === "black" ? "text-emerald-300/80" : "text-emerald-700/80" 
                              : "text-gray-400"
                          }`}>{item.description}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0 ml-3">
                      {isSelected && (
                        <span className="text-[11px] opacity-80 font-mono hidden sm:inline-block px-1.5 py-0.5 rounded bg-black/5 dark:bg-white/10">↵ Jump</span>
                      )}
                      <IconChevronRight stroke={iconStroke} size={16} className={`transition-transform ${isSelected ? "translate-x-0.5 text-emerald-600 dark:text-emerald-400" : "opacity-40"}`} />
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer Guide */}
          <div className={`px-4 py-2.5 border-t text-xs flex items-center justify-between text-gray-400 ${
            theme === "black" ? "border-neutral-800 bg-[#171717]" : "border-gray-100 bg-gray-50/90"
          }`}>
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <kbd className="kbd kbd-xs bg-white dark:bg-neutral-700">↑</kbd><kbd className="kbd kbd-xs bg-white dark:bg-neutral-700">↓</kbd> Navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="kbd kbd-xs bg-white dark:bg-neutral-700">↵</kbd> Open
              </span>
              <span className="flex items-center gap-1">
                <kbd className="kbd kbd-xs bg-white dark:bg-neutral-700">esc</kbd> Close
              </span>
            </div>
            <div className="font-medium">
              {filteredResults.length} {filteredResults.length === 1 ? 'menu' : 'menus'}
            </div>
          </div>
        </div>

        <form method="dialog" className="modal-backdrop">
          <button>close</button>
        </form>
      </dialog>
    </>
  );
}
